

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Briefcase, MapPin, DollarSign, Clock, Plus, Globe, User, ArrowRight, ChevronLeft, ChevronRight, Lock, Mail, Building } from 'lucide-react';
import { supabase } from '@/lib/supabase-client';
import { useAuth } from '@/context/AuthContext';
import { getUserInvitations } from '@/lib/job-invitations';

interface Job {
  id: string;
  title: string;
  company_name: string;
  description: string;
  requirements: string;
  tech_stack: string;
  location: string;
  salary_range: string;
  employment_type: string;
  status: string;
  created_at: string;
  user_id: string | null;
  organization_id: string | null;
  organizations?: {
    name: string;
  };
}

type FilterType = 'all' | 'public' | 'my_jobs' | 'organization';

const JOBS_PER_PAGE = 6;

export default function JobsPage() {
  const navigate = useNavigate();
  const { isRecruiterValue, organizationId } = useAuth();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [allJobs, setAllJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [invitedJobIds, setInvitedJobIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUserId(user?.id || null);
      setCurrentUserEmail(user?.email || null);

      let query = supabase
        .from('job_postings')
        .select(`
          *,
          organizations (
            name
          )
        `)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching jobs:', error);
        setAllJobs([]);
        setJobs([]);
      } else {
        let fetchedJobs = data || [];

        // Fetch user invitations if authenticated
        if (user?.email) {
          const invitations = await getUserInvitations(user.email);
          const invitedIds = new Set(invitations.map(inv => inv.job_posting_id));
          setInvitedJobIds(invitedIds);

          // Filter jobs based on access
          if (isRecruiterValue && organizationId) {
            // Recruiters see all jobs from their organization plus public jobs
            fetchedJobs = fetchedJobs.filter(job =>
              job.organization_id === organizationId || !job.organization_id
            );
          } else {
            // Regular users see public jobs and private jobs they're invited to
            fetchedJobs = fetchedJobs.filter(job =>
              !job.organization_id || invitedIds.has(job.id)
            );
          }
        } else {
          // Unauthenticated users see only public jobs
          fetchedJobs = fetchedJobs.filter(job => !job.organization_id);
        }

        console.log('Total jobs:', data?.length, 'Filtered jobs:', fetchedJobs.length);
        setAllJobs(fetchedJobs);
        setJobs(fetchedJobs);
      }
    } catch (error: any) {
      console.error('Error fetching jobs:', error);
      setAllJobs([]);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = (filter: FilterType) => {
    setActiveFilter(filter);
    setCurrentPage(1);

    switch (filter) {
      case 'all':
        setJobs(allJobs);
        break;
      case 'public':
        setJobs(allJobs.filter(job => !job.organization_id));
        break;
      case 'my_jobs':
        setJobs(allJobs.filter(job => job.user_id === currentUserId));
        break;
      case 'organization':
        setJobs(allJobs.filter(job => job.organization_id === organizationId));
        break;
    }
  };

  const totalPages = Math.ceil(jobs.length / JOBS_PER_PAGE);
  const startIndex = (currentPage - 1) * JOBS_PER_PAGE;
  const endIndex = startIndex + JOBS_PER_PAGE;
  const currentJobs = jobs.slice(startIndex, endIndex);

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPage = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getEmploymentTypeColor = (type: string) => {
    switch (type) {
      case 'full-time':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'contract':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'part-time':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-green-50/30 p-4 sm:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-10 bg-gray-200 rounded w-1/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-80 bg-white rounded-2xl shadow-sm border border-gray-100"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-green-50/30">
      <div className="max-w-7xl mx-auto p-4 sm:p-8 pt-20 sm:pt-12 pb-8 sm:pb-12">
        <div className="mb-8 sm:mb-12 animate-fade-in-up">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Job Openings</h1>
              <p className="text-gray-600">Practice interviews for real positions</p>
            </div>
            <Button
              onClick={() => navigate('/jobs/create')}
              size="lg"
              className="bg-green-600 hover:bg-green-700 shadow-lg hover:shadow-xl transition-all w-full sm:w-auto"
            >
              <Plus className="w-5 h-5 mr-2" />
              Post New Job
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <Card
              className={`bg-white shadow-sm hover:shadow-md transition-all cursor-pointer border-2 ${
                activeFilter === 'all' ? 'border-green-500 ring-2 ring-green-200' : 'border-green-200'
              }`}
              onClick={() => applyFilter('all')}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">All Jobs</p>
                    <p className="text-3xl font-bold text-gray-900">{allJobs.length}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <Briefcase className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card
              className={`bg-white shadow-sm hover:shadow-md transition-all cursor-pointer border-2 ${
                activeFilter === 'public' ? 'border-blue-500 ring-2 ring-blue-200' : 'border-blue-200'
              }`}
              onClick={() => applyFilter('public')}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Public Jobs</p>
                    <p className="text-3xl font-bold text-gray-900">
                      {allJobs.filter(j => !j.organization_id).length}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <Globe className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {isRecruiterValue && organizationId ? (
              <Card
                className={`bg-white shadow-sm hover:shadow-md transition-all cursor-pointer border-2 ${
                  activeFilter === 'organization' ? 'border-orange-500 ring-2 ring-orange-200' : 'border-orange-200'
                }`}
                onClick={() => applyFilter('organization')}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">My Organization</p>
                      <p className="text-3xl font-bold text-gray-900">
                        {allJobs.filter(j => j.organization_id === organizationId).length}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                      <Building className="w-6 h-6 text-orange-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card
                className={`bg-white shadow-sm hover:shadow-md transition-all cursor-pointer border-2 ${
                  activeFilter === 'my_jobs' ? 'border-orange-500 ring-2 ring-orange-200' : 'border-orange-200'
                }`}
                onClick={() => applyFilter('my_jobs')}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">My Jobs</p>
                      <p className="text-3xl font-bold text-gray-900">
                        {allJobs.filter(j => j.user_id === currentUserId).length}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                      <User className="w-6 h-6 text-orange-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {jobs.length === 0 ? (
          <Card className="p-12 text-center border-none shadow-sm bg-white animate-scale-in">
            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Briefcase className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No Jobs Available</h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Start building your interview practice platform by creating your first job posting
            </p>
            <Button
              onClick={() => navigate('/jobs/create')}
              size="lg"
              className="bg-green-600 hover:bg-green-700 shadow-lg"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create First Job Posting
            </Button>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentJobs.map((job, index) => (
              <Card
                key={job.id}
                className="group relative bg-white shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden animate-fade-in-up border-2 border-gray-200 hover:border-green-300"
                style={{ animationDelay: `${index * 0.1}s` }}
                onClick={() => navigate(`/jobs/${job.id}`)}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>

                <CardContent className="p-6 relative">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex flex-wrap gap-2">
                      <Badge className={`${getEmploymentTypeColor(job.employment_type)} border px-3 py-1`}>
                        {job.employment_type}
                      </Badge>
                      {job.organization_id ? (
                        <Badge className="bg-blue-100 text-blue-700 border-blue-200 border flex items-center gap-1">
                          <Lock className="w-3 h-3" />
                          Private
                        </Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-700 border-green-200 border flex items-center gap-1">
                          <Globe className="w-3 h-3" />
                          Public
                        </Badge>
                      )}
                      {invitedJobIds.has(job.id) && (
                        <Badge className="bg-orange-100 text-orange-700 border-orange-200 border flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          Invited
                        </Badge>
                      )}
                    </div>
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      {job.status}
                    </Badge>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">
                    {job.title}
                  </h3>
                  <p className="text-base font-medium text-gray-700 mb-3">
                    {job.organizations?.name || job.company_name}
                  </p>

                  <p className="text-gray-600 line-clamp-2 text-sm leading-relaxed mb-4">
                    {job.description}
                  </p>

                  <div className="space-y-2 pt-4 border-t border-gray-100">
                    {job.location && (
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                        <span>{job.location}</span>
                      </div>
                    )}
                    {job.salary_range && (
                      <div className="flex items-center text-sm text-gray-600">
                        <DollarSign className="w-4 h-4 mr-2 text-gray-400" />
                        <span>{job.salary_range}</span>
                      </div>
                    )}
                    {job.tech_stack && (
                      <div className="flex items-start text-sm text-gray-600">
                        <Briefcase className="w-4 h-4 mr-2 mt-0.5 text-gray-400 flex-shrink-0" />
                        <span className="line-clamp-1">{job.tech_stack}</span>
                      </div>
                    )}
                    <div className="flex items-center text-sm text-gray-500">
                      <Clock className="w-4 h-4 mr-2 text-gray-400" />
                      <span>Posted {new Date(job.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between text-sm text-green-600 font-semibold group-hover:gap-2 transition-all">
                      <span>View Details</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 animate-fade-in">
                <p className="text-sm text-gray-600">
                  Showing <span className="font-semibold text-gray-900">{startIndex + 1}</span> to{' '}
                  <span className="font-semibold text-gray-900">{Math.min(endIndex, jobs.length)}</span> of{' '}
                  <span className="font-semibold text-gray-900">{jobs.length}</span> jobs
                </p>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                    className="border-gray-300 hover:border-green-400 hover:bg-green-50 disabled:opacity-50"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <Button
                        key={page}
                        variant={currentPage === page ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => goToPage(page)}
                        className={`w-10 h-10 ${
                          currentPage === page
                            ? 'bg-green-600 hover:bg-green-700 text-white'
                            : 'border-gray-300 hover:border-green-400 hover:bg-green-50'
                        }`}
                      >
                        {page}
                      </Button>
                    ))}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                    className="border-gray-300 hover:border-green-400 hover:bg-green-50 disabled:opacity-50"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
